// Sprint TENANTS TENANTS.B2 (#2338) / TENANTS.B3 (#2339) — tenant
// expanded-row detail panel + admin actions.
//
// Fills in B1's placeholder inside InternalTenantsPage's row-expand `<tr>`.
// Mounted only while that row is open (see InternalTenantsPage.tsx's
// `{isOpen && (...)}`) — collapsing and re-expanding destroys and remounts
// this component, so "fresh data on re-expand" (AC) falls out of that
// lifecycle for free, not from any cache-invalidation logic in here.
//
// Two independent fetches, two independent loading states:
//   - GET /api/internal/tenants/:id       (TENANTS.A2, #2335) — members +
//     project counts. Fast, no aggregation.
//   - GET /api/internal/tenants/:id/usage (TENANTS.A2, #2335) — token/cost
//     usage, aggregated from token_usage_monthly. Fetched in PARALLEL, not
//     gated behind the detail fetch — the members/project-count section must
//     render as soon as ITS OWN fetch resolves, never waiting on usage.
//
// ── "View on platform" is a disabled placeholder, not a working link ───────
// Recon for this story found no existing mechanism for a PLATFORM_ADMIN to
// enter an arbitrary tenant's context — TenantSwitcher.tsx's switchTenant()
// requires an actual tenant_members row, which a platform admin inspecting
// someone else's tenant does not have, and no alternate "view-as" backend
// route exists anywhere in the app. Founder decision (#2337/#2338 handoff,
// 2026-08-29): ship this disabled with an explanatory tooltip rather than a
// link to a URL no route reads — a live-looking dead link is worse than an
// honest disabled state. Tracked as #2390 (the backend view-as capability,
// filed P2 with an explicit R1+R2+R3 requirement given the security surface
// of letting a platform admin adopt another tenant's context); enable this
// button when that lands.
//
// ── B3 (#2339): tabs, actions footer, feature flags ─────────────────────────
// The story asked for an Audit Trail tab "alongside Usage / Members." Rather
// than force Members/Usage (independently tested, already-merged B2
// content, including its carefully-verified async-independence guarantees)
// into their own individually-clickable tabs, this keeps them together as
// the default "Details" tab, unchanged, and adds "Audit Trail" as a second
// tab — satisfying the literal requirement (Audit Trail IS a tab in this
// panel) without a breaking restructure of B2's tested behavior. Flagged as
// a deliberate interpretation, not a silent scope cut — see the PR/issue
// for the founder decision. The Audit tab's content (TenantAuditTab) is
// lazy — it only mounts, and therefore only fetches, once selected, since
// most row-expands won't need it and firing a third request unconditionally
// on every expand would be wasted work for the common case.
//
// Feature flags render inside the Details tab (a "Section in expanded row"
// per the story, not a footer button) via TenantFeatureFlagsSection.
// Suspend/Reactivate/Tier/Seat-limit are footer buttons (TenantActionsFooter)
// — that's where B2 left the `data-tenant-actions` placeholder this story
// replaces.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { apiFetch, ApiError } from '../../lib/api';
import TenantActionsFooter from './TenantActionsFooter';
import TenantFeatureFlagsSection from './TenantFeatureFlagsSection';
import TenantAuditTab from './TenantAuditTab';

// ── Types mirroring TENANTS.A2's response shapes ────────────────────────────

interface TenantMember {
  user_id: string;
  name: string;
  email: string;
  role: string;
  joined_at: string;
}

interface TenantDetail {
  id: string;
  name: string;
  tier: string;
  status: 'ACTIVE' | 'SUSPENDED';
  seat_count: number;
  // TENANTS.B3 (#2339) — the admin-set cap, added to this GET response as
  // part of this story so "Set seat limit" can pre-fill correctly. Distinct
  // from seat_count above (current usage) — see internal-tenants.router.ts's
  // own note on that distinction.
  seat_limit: number | null;
  suspension_scheduled_at: string | null;
  feature_flags: Record<string, boolean>;
  created_at: string;
  members: TenantMember[];
  project_count: { active: number; total: number };
}

interface UsageMonthly {
  month: string; // 'YYYY-MM'
  tokens: number;
  cost_usd: number;
}

interface UsageResponse {
  all_time_tokens: number;
  all_time_cost_usd: number;
  monthly: UsageMonthly[];
}

// Mirrors apps/api/src/middleware/tenant-auth.middleware.ts's
// TENANT_ROLE_RANK exactly. Duplicated, not re-derived — the web and api
// packages don't share a module boundary — deliberately, not an oversight:
// a future rank change there is meant to be a conscious decision to update
// here too, not silent drift.
const ROLE_RANK: Record<string, number> = {
  TENANT_ADMIN: 2,
  ADMIN: 2,
  FACULTY_SUPERVISOR: 1.5,
  TENANT_MEMBER: 1,
  STUDENT: 1,
  CLIENT: 0,
};

const MONTHS_PAGE_SIZE = 12;

function sortMembers(members: TenantMember[]): TenantMember[] {
  return [...members].sort((a, b) => {
    const rankDiff = (ROLE_RANK[b.role] ?? 0) - (ROLE_RANK[a.role] ?? 0);
    if (rankDiff !== 0) return rankDiff;
    return a.name.localeCompare(b.name);
  });
}

function fmtTokens(n: number): string {
  return n.toLocaleString('en-US');
}

function fmtUsd(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

// 'YYYY-MM' parsed as UTC midnight — new Date('YYYY-MM-01') without a time
// component is parsed as UTC by spec, but toLocaleDateString without an
// explicit timeZone reads it back in the LOCAL zone, which can shift the
// displayed month by one in zones behind UTC. Pinning timeZone: 'UTC' on the
// way out matches the way it went in.
function fmtMonth(month: string): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as ApiError | null;
  return (e?.body?.error as string | undefined) ?? fallback;
}

// ── Styling — inline + CSS vars, matching InternalTenantsPage/InternalPricingPage ──

const sectionStyle: React.CSSProperties = { marginBottom: 20 };

const sectionTitleStyle: React.CSSProperties = {
  fontFamily:    'var(--font-display)',
  fontSize:      12,
  fontWeight:    600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color:         'var(--text-muted)',
  marginBottom:  8,
};

const tableStyle: React.CSSProperties = {
  width:          '100%',
  borderCollapse: 'collapse',
  fontSize:       13,
};

const thStyle: React.CSSProperties = {
  textAlign:     'left',
  padding:       '6px 10px',
  fontSize:      11,
  fontWeight:    600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color:         'var(--text-muted)',
  borderBottom:  '1px solid var(--border)',
};

const tdStyle: React.CSSProperties = {
  padding:      '6px 10px',
  borderBottom: '1px solid var(--border)',
  color:        'var(--text)',
};

function skeletonBar(height = 14, width: number | string = '100%'): React.CSSProperties {
  return { height, width, borderRadius: 4, background: 'var(--surface-alt)', marginBottom: 6 };
}

const smallBtnStyle: React.CSSProperties = {
  padding:      '4px 10px',
  fontSize:     12,
  fontFamily:   'var(--font-body)',
  border:       '1px solid var(--border)',
  borderRadius: 6,
  background:   'var(--surface)',
  color:        'var(--text)',
  cursor:       'pointer',
};

// ── Component ───────────────────────────────────────────────────────────────

interface Props {
  tenantId: string;
  /**
   * TENANTS.B3 (#2339) — called after a successful suspend/reactivate/tier
   * action with just the fields that changed, so InternalTenantsPage can
   * patch its own list-row state without a full page refetch (which would
   * also reset pagination/search/sort). Seat-limit and flag changes don't
   * call this — neither field is displayed on the list row (seat_count
   * there is a different, always-null field per #2361; flags aren't shown
   * on the list at all).
   */
  onTenantUpdated?: (tenantId: string, patch: { status?: 'Active' | 'Suspended'; tier?: string }) => void;
}

type Tab = 'details' | 'audit';

export default function TenantExpandedRow({ tenantId, onTenantUpdated }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('details');

  const [detail, setDetail]             = useState<TenantDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError]   = useState('');

  const [usage, setUsage]               = useState<UsageResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError]     = useState('');

  const [monthsVisible, setMonthsVisible] = useState(MONTHS_PAGE_SIZE);

  // Same closure-based staleness-guard idiom as InternalTenantsPage (see its
  // own comment) — plain per-effect-run `cancelled` flags, no useRef, since
  // a ref read/write inside a useCallback invoked from a bare effect is what
  // react-hooks/set-state-in-effect (v7) objects to, not the guard itself.
  const fetchDetail = useCallback(async (isStale: () => boolean) => {
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await apiFetch<TenantDetail>(`/api/internal/tenants/${encodeURIComponent(tenantId)}`);
      if (isStale()) return;
      setDetail(res);
    } catch (err) {
      if (isStale()) return;
      setDetailError(errorMessage(err, 'Failed to load tenant details.'));
    } finally {
      if (!isStale()) setDetailLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    fetchDetail(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchDetail]);

  const fetchUsage = useCallback(async (isStale: () => boolean) => {
    setUsageLoading(true);
    setUsageError('');
    try {
      const res = await apiFetch<UsageResponse>(`/api/internal/tenants/${encodeURIComponent(tenantId)}/usage`);
      if (isStale()) return;
      setUsage(res);
    } catch (err) {
      if (isStale()) return;
      setUsageError(errorMessage(err, 'Failed to load usage.'));
    } finally {
      if (!isStale()) setUsageLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    fetchUsage(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchUsage]);

  // Retry calls fetchUsage directly rather than going through the effect —
  // it's a user-initiated action, not a reactive response to a changed
  // dependency, so it doesn't need an effect indirection (or the "bump a
  // counter to force a new callback identity" trick that would require).
  //
  // R1 finding (#2391): `() => false` means a retry's result always applies
  // regardless of arrival order, unlike the effect-driven calls above. That
  // was safe only INCIDENTALLY — usageLoading flips true synchronously, so
  // the Retry button (rendered only in the usageError branch) unmounts
  // before a second click can ever fire this handler again, meaning only
  // one retry could ever be in flight. Correct today, but fragile: it would
  // silently stop holding if the render order ever changed. `retryInFlight`
  // makes the guarantee structural instead of incidental, matching the
  // rigor already applied to fetchDetail/fetchUsage's own staleness guards
  // — a ref is safe here because retryUsage is invoked from an event
  // handler, never from a bare effect (see fetchTenants's own comment on
  // this file's react-hooks/set-state-in-effect constraint).
  const retryInFlightRef = useRef(false);
  const retryUsage = useCallback(() => {
    if (retryInFlightRef.current) return;
    retryInFlightRef.current = true;
    fetchUsage(() => false).finally(() => { retryInFlightRef.current = false; });
  }, [fetchUsage]);

  // TENANTS.B3 (#2339) — re-fetches this row's OWN detail after any
  // successful action, so the members/flags/seat-limit sections reflect the
  // change immediately without waiting for a collapse/re-expand. `() =>
  // false` is safe here for the same reason retryUsage's is: invoked only
  // from action-success callbacks (event-driven), never from a bare effect.
  const refreshDetail = useCallback(() => { fetchDetail(() => false); }, [fetchDetail]);

  const handleActionSuccess = useCallback((patch: { status?: 'Active' | 'Suspended'; tier?: string }) => {
    refreshDetail();
    onTenantUpdated?.(tenantId, patch);
  }, [refreshDetail, onTenantUpdated, tenantId]);

  const sortedMembers = useMemo(() => (detail ? sortMembers(detail.members) : []), [detail]);

  const visibleMonthly = useMemo(
    () => (usage ? usage.monthly.slice(0, monthsVisible) : []),
    [usage, monthsVisible],
  );
  const hasMoreMonths = usage ? usage.monthly.length > monthsVisible : false;

  // API is newest-first; the chart reads left-to-right as oldest-to-newest.
  const chartData = useMemo(
    () => [...visibleMonthly].reverse().map(m => ({ month: fmtMonth(m.month), tokens: m.tokens })),
    [visibleMonthly],
  );

  return (
    <div data-tenant-id={tenantId} data-testid={`tenant-expanded-row-${tenantId}`} style={{ padding: '4px 4px 8px' }}>
      {/* ── Tabs (TENANTS.B3, #2339) ─────────────────────────────────────── */}
      <div role="tablist" aria-label="Tenant detail" style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border)' }}>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'details'}
          onClick={() => setActiveTab('details')}
          data-testid={`tenant-tab-details-${tenantId}`}
          style={{
            padding: '6px 12px', fontSize: 12, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
            color: activeTab === 'details' ? 'var(--text)' : 'var(--text-muted)',
            borderBottom: activeTab === 'details' ? '2px solid var(--primary, #6366f1)' : '2px solid transparent',
          }}
        >
          Details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'audit'}
          onClick={() => setActiveTab('audit')}
          data-testid={`tenant-tab-audit-${tenantId}`}
          style={{
            padding: '6px 12px', fontSize: 12, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer',
            color: activeTab === 'audit' ? 'var(--text)' : 'var(--text-muted)',
            borderBottom: activeTab === 'audit' ? '2px solid var(--primary, #6366f1)' : '2px solid transparent',
          }}
        >
          Audit Trail
        </button>
      </div>

      {activeTab === 'audit' ? (
        <div role="tabpanel" data-testid={`tenant-audit-tabpanel-${tenantId}`}>
          <TenantAuditTab tenantId={tenantId} />
        </div>
      ) : (
      <div role="tabpanel" data-testid={`tenant-details-tabpanel-${tenantId}`}>
      {/* ── Members ──────────────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Members</div>
        {detailLoading ? (
          <div data-testid={`tenant-members-loading-${tenantId}`}>
            <div style={skeletonBar()} />
            <div style={skeletonBar()} />
          </div>
        ) : detailError ? (
          <div data-testid={`tenant-detail-error-${tenantId}`} style={{ color: 'var(--error)', fontSize: 13 }}>
            {detailError}
          </div>
        ) : sortedMembers.length === 0 ? (
          <div data-testid={`tenant-members-empty-${tenantId}`} style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            No members.
          </div>
        ) : (
          <table style={tableStyle} data-testid={`tenant-members-table-${tenantId}`}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map(m => (
                <tr key={m.user_id} data-testid={`tenant-member-${m.user_id}`}>
                  <td style={tdStyle}>{m.name}</td>
                  <td style={tdStyle}>{m.email}</td>
                  <td style={tdStyle}>{m.role}</td>
                  <td style={tdStyle}>{fmtDate(m.joined_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Project counts ───────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Projects</div>
        {detailLoading ? (
          <div style={skeletonBar(16, 160)} />
        ) : detailError ? null : detail ? (
          <div data-testid={`tenant-project-counts-${tenantId}`} style={{ fontSize: 13, color: 'var(--text)' }}>
            {detail.project_count.active} active projects · {detail.project_count.total} total projects
          </div>
        ) : null}
      </div>

      {/* ── Usage ────────────────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Usage</div>
        {usageLoading ? (
          <div data-testid={`tenant-usage-loading-${tenantId}`}>
            <div style={skeletonBar(16, 220)} />
            <div style={skeletonBar(100, '100%')} />
          </div>
        ) : usageError ? (
          <div data-testid={`tenant-usage-error-${tenantId}`} style={{ fontSize: 13 }}>
            <div style={{ color: 'var(--error)', marginBottom: 6 }}>{usageError}</div>
            <button
              type="button"
              onClick={retryUsage}
              data-testid={`tenant-usage-retry-${tenantId}`}
              style={smallBtnStyle}
            >
              Retry
            </button>
          </div>
        ) : usage ? (
          <>
            <div data-testid={`tenant-usage-all-time-${tenantId}`} style={{ fontSize: 13, marginBottom: 10 }}>
              All time: <strong>{fmtTokens(usage.all_time_tokens)}</strong> tokens · <strong>{fmtUsd(usage.all_time_cost_usd)}</strong>
            </div>

            {usage.monthly.length === 0 ? (
              <div data-testid={`tenant-usage-empty-${tenantId}`} style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                No usage recorded yet.
              </div>
            ) : (
              <>
                <div style={{ width: '100%', height: 100, marginBottom: 10 }} data-testid={`tenant-usage-chart-${tenantId}`}>
                  <ResponsiveContainer>
                    <BarChart data={chartData}>
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={40} />
                      <RechartsTooltip />
                      <Bar dataKey="tokens" fill="var(--primary, #6366f1)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <table style={tableStyle} data-testid={`tenant-usage-table-${tenantId}`}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Month</th>
                      <th style={thStyle}>Tokens</th>
                      <th style={thStyle}>Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMonthly.map(m => (
                      <tr key={m.month} data-testid={`tenant-usage-row-${m.month}`}>
                        <td style={tdStyle}>{fmtMonth(m.month)}</td>
                        <td style={tdStyle}>{fmtTokens(m.tokens)}</td>
                        <td style={tdStyle}>{fmtUsd(m.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {hasMoreMonths && (
                  <button
                    type="button"
                    onClick={() => setMonthsVisible(v => v + MONTHS_PAGE_SIZE)}
                    data-testid={`tenant-usage-show-more-${tenantId}`}
                    style={{ ...smallBtnStyle, marginTop: 6, background: 'transparent' }}
                  >
                    Show more
                  </button>
                )}
              </>
            )}
          </>
        ) : null}
      </div>

      {/* ── Feature flags (TENANTS.B3, #2339) ───────────────────────────── */}
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>Feature flags</div>
        {detailLoading ? (
          <div style={skeletonBar(16, 200)} />
        ) : detailError ? null : detail ? (
          <TenantFeatureFlagsSection
            tenantId={tenantId}
            flags={detail.feature_flags}
            onActionSuccess={refreshDetail}
          />
        ) : null}
      </div>
      </div>
      )}

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        <button
          type="button"
          disabled
          title="View-as mechanism not yet implemented — see #2390"
          data-testid={`tenant-view-on-platform-${tenantId}`}
          style={{ padding: '5px 12px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'transparent', color: 'var(--text-muted)', cursor: 'not-allowed', opacity: 0.55 }}
        >
          View on platform
        </button>
        {/* TENANTS.B3 (#2339) — suspend/reactivate/tier/seat-limit. */}
        {detail && (
          <TenantActionsFooter
            tenantId={tenantId}
            tenantName={detail.name}
            status={detail.status}
            currentTier={detail.tier}
            currentSeatLimit={detail.seat_limit}
            onActionSuccess={handleActionSuccess}
          />
        )}
      </div>
    </div>
  );
}
